import React, { useState, useRef, useEffect, useMemo } from 'react';
import { doc, updateDoc, arrayUnion, getDoc, writeBatch, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import DictionarySearch from './DictionarySearch';
import { getQuickAddChipsForLanguage } from '../../utils/keyRegistry';
import { getInverseKey } from '../../utils/referenceInverse';
import { usePreferences } from '../../context/UserPreferencesContext';
import './QuickAddChips.css';

/**
 * QuickAddChips — renders clickable chip buttons for common annotation keys.
 *
 * Props:
 *   entryId — Firestore doc ID (live mode). If null/undefined, operates in staging mode.
 *   entryCanonicalForm — the source entry's canonical form, needed to build the
 *     inverse item for bidirectional reference writes (live mode only).
 *   onItemAdded(item) — callback for live mode after Firestore write succeeds
 *   onItemStaged(item) — callback for staging mode (local state accumulation)
 */
function QuickAddChips({ entryId, entryCanonicalForm, onItemAdded, onItemStaged }) {
  const { targetLanguage } = usePreferences();
  const [activeChip, setActiveChip] = useState(null); // index of active chip
  const [textValue, setTextValue] = useState('');
  const [saving, setSaving] = useState(false);
  const textInputRef = useRef(null);

  // Chips derived from the key registry, filtered by target language.
  const CHIPS = useMemo(() => getQuickAddChipsForLanguage(targetLanguage), [targetLanguage]);

  // Reset active chip / input when the target language (and thus chip set) changes
  useEffect(() => {
    setActiveChip(null);
    setTextValue('');
  }, [targetLanguage]);

  // Auto-focus text input when a plainText chip is activated
  useEffect(() => {
    if (activeChip !== null && CHIPS[activeChip]?.category === 'plainText') {
      textInputRef.current?.focus();
    }
  }, [activeChip, CHIPS]);

  // --- Save item (live or staging) ---
  const saveItem = async (item) => {
    if (entryId) {
      // Live mode: write to Firestore
      setSaving(true);
      try {
        // Bidirectional add: if this is a dictionaryReference item (has an
        // entryId) whose key has an inverse, mirror the inverse onto the
        // referenced entry in a single batch.
        const inverseKey = getInverseKey(item.key);
        if (inverseKey && item.entryId && entryCanonicalForm) {
          const refRef = doc(db, 'dictionary', item.entryId);
          const refSnap = await getDoc(refRef);
          if (refSnap.exists()) {
            const refData = refSnap.data();
            const inverseItem = { type: 'text', key: inverseKey, value: entryCanonicalForm, entryId: entryId, note: null };
            const existing = (refData.displayItems || []).some(
              (di) => di.type === 'text' && di.key === inverseKey && di.entryId === entryId && di.value === entryCanonicalForm
            );
            const batch = writeBatch(db);
            batch.update(doc(db, 'dictionary', entryId), { displayItems: arrayUnion(item), lastUpdated: serverTimestamp() });
            if (!existing) {
              batch.update(refRef, { displayItems: arrayUnion(inverseItem), lastUpdated: serverTimestamp() });
            }
            await batch.commit();
            onItemAdded && onItemAdded(item);
            setSaving(false);
            setActiveChip(null);
            setTextValue('');
            return;
          }
        }

        const entryRef = doc(db, 'dictionary', entryId);
        await updateDoc(entryRef, {
          displayItems: arrayUnion(item),
          lastUpdated: serverTimestamp(),
        });
        onItemAdded && onItemAdded(item);
      } catch (e) {
        console.error('QuickAddChips: error saving item', e);
      }
      setSaving(false);
    } else {
      // Staging mode: pass to parent
      onItemStaged && onItemStaged(item);
    }
    // Reset UI
    setActiveChip(null);
    setTextValue('');
  };

  // --- Chip click handler ---
  const handleChipClick = (index) => {
    const chip = CHIPS[index];

    if (chip.category === 'quickAdd') {
      // Predetermined value — save immediately
      const item = { type: 'text', key: chip.key, value: chip.value, entryId: null, note: null };
      saveItem(item);
      return;
    }

    // Toggle active chip
    if (activeChip === index) {
      setActiveChip(null);
      setTextValue('');
    } else {
      setActiveChip(index);
      setTextValue('');
    }
  };

  // --- Plain text: save on Enter ---
  const handleTextKeyDown = (e) => {
    if (e.key === 'Enter' && textValue.trim()) {
      const chip = CHIPS[activeChip];
      const item = { type: 'text', key: chip.key, value: textValue.trim(), entryId: null, note: null };
      saveItem(item);
    } else if (e.key === 'Escape') {
      setActiveChip(null);
      setTextValue('');
    }
  };

  // --- Restricted dropdown: save on option click ---
  const handleOptionClick = (option) => {
    const chip = CHIPS[activeChip];
    let finalKey = chip.key;
    let finalValue = option;

    if (chip.key === 'gender') {
      finalKey = 'is a';
      finalValue = `${option} noun`;
    } else if (chip.key === 'register') {
      finalKey = 'is used';
      finalValue = option;
    } else if (chip.key === 'word type') {
      finalKey = 'is a';
      finalValue = option;
    }

    const item = { type: 'text', key: finalKey, value: finalValue, entryId: null, note: null };
    saveItem(item);
  };

  // --- Dictionary reference: save on entry select ---
  const handleRefSelect = async (entry) => {
    const chip = CHIPS[activeChip];

    // Auto-snapshot note from first displayItem or meaningEnglish
    let autoNote = null;
    try {
      const refDoc = await getDoc(doc(db, 'dictionary', entry.id));
      if (refDoc.exists()) {
        const data = refDoc.data();
        const firstItem = (data.displayItems || [])[0];
        if (firstItem) {
          if (firstItem.type === 'text' && firstItem.value) {
            autoNote = firstItem.value;
          } else if (firstItem.type === 'label' && firstItem.text) {
            autoNote = firstItem.text;
          }
        } else if (data.meaningEnglish) {
          autoNote = data.meaningEnglish;
        }
      }
    } catch (e) {
      // Non-critical — skip auto-note
    }

    const item = {
      type: 'text',
      key: chip.key,
      value: entry.canonicalForm,
      entryId: entry.id,
      note: autoNote,
    };
    saveItem(item);
  };

  // --- Render inline value area ---
  const renderValueArea = () => {
    if (activeChip === null) return null;
    const chip = CHIPS[activeChip];

    switch (chip.category) {
      case 'plainText':
        return (
          <div className="quickadd-value-area">
            <input
              ref={textInputRef}
              className="quickadd-text-input"
              type="text"
              placeholder={`Enter ${chip.label}…`}
              value={textValue}
              onChange={(e) => setTextValue(e.target.value)}
              onKeyDown={handleTextKeyDown}
              disabled={saving}
              aria-label={`Value for ${chip.label}`}
            />
          </div>
        );

      case 'restrictedDropdown':
        return (
          <div className="quickadd-value-area">
            <div className="quickadd-options">
              {chip.options.map((opt) => (
                <button
                  key={opt}
                  className="quickadd-option-chip"
                  onClick={() => handleOptionClick(opt)}
                  disabled={saving}
                  type="button"
                >
                  {opt}
                </button>
              ))}
            </div>
          </div>
        );

      case 'dictionaryReference':
        return (
          <div className="quickadd-value-area">
            <DictionarySearch
              onSelect={handleRefSelect}
              onNotFound={() => {}}
              onClear={() => {
                setActiveChip(null);
              }}
            />
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="quickadd-chips">
      <div className="quickadd-chips-row">
        {CHIPS.map((chip, index) => (
          <button
            key={`${chip.label}-${index}`}
            className={`quickadd-chip ${activeChip === index ? 'quickadd-chip--active' : ''}`}
            onClick={() => handleChipClick(index)}
            disabled={saving}
            type="button"
          >
            + {chip.label}
          </button>
        ))}
      </div>
      {renderValueArea()}
    </div>
  );
}

export default QuickAddChips;
